import { ConfirmationModalComponent } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { UnsavedChangesContent } from './use-modal-unsaved-changes.style';

export interface UnsavedChangesType {
    shouldShowUnsavedChanges: () => boolean;
    setIsUnsavedChanges: (shouldShow: boolean) => void;
    showUnsavedChangesIfNeededOrClose: (onConfirm?: VoidFunction) => void;
}

export interface UnsavedChangesModalCallbacks {
    afterConfirm?: VoidFunction;
    afterDismiss?: VoidFunction;
}

export const useModalUnsavedChanges = (
    callback?: VoidFunction,
    onCancel?: VoidFunction,
    { afterConfirm, afterDismiss }: UnsavedChangesModalCallbacks = {}
): UnsavedChangesType => {
    const { open: popupManagerOpen } = usePopupManager();
    const { t } = useTranslation();

    const shouldShow = useRef<boolean>(false);
    const isModalOpen = useRef<boolean>(false);

    const setShouldShowUnsavedChanges = useCallback((show: boolean) => (shouldShow.current = show), []);
    const setIsUnsavedChanges = useCallback(
        (status: boolean) => setShouldShowUnsavedChanges(status),
        [setShouldShowUnsavedChanges]
    );

    const shouldShowUnsavedChanges = useCallback(() => shouldShow.current, []);

    const showUnsavedChangesIfNeededOrClose = useCallback(
        (onConfirm?: VoidFunction) => {
            if (shouldShow.current) {
                if (isModalOpen.current) {
                    return;
                }
                isModalOpen.current = true;
                let confirmed = false;

                popupManagerOpen(ConfirmationModalComponent, {
                    title: t('commons.modal.unsaved-data.title'),
                    children: (
                        <UnsavedChangesContent>
                            <span>{t('commons.modal.unsaved-data.description.main')}</span>
                            <span>{t('commons.modal.unsaved-data.description.secondary')}</span>
                        </UnsavedChangesContent>
                    ),
                    buttonText: t('commons.modal.unsaved-data.button.primary'),
                    cancelButtonText: t('commons.modal.unsaved-data.button.secondary'),
                    actionOnClick: () =>
                        new Promise((resolve) => {
                            confirmed = true;
                            setIsUnsavedChanges(false);
                            if (onConfirm) {
                                onConfirm();
                            } else {
                                callback?.();
                            }
                            afterConfirm?.();
                            resolve(true);
                        }),
                    onClose: () => {
                        isModalOpen.current = false;
                        if (confirmed) {
                            return;
                        }
                        afterDismiss?.();
                        onCancel?.();
                    },
                });
            } else if (onConfirm) {
                onConfirm();
            } else {
                callback?.();
            }
        },
        [callback, onCancel, afterConfirm, afterDismiss, popupManagerOpen, t, setIsUnsavedChanges]
    );

    useEffect(() => {
        // Register once and read the ref when the event fires: the form turns dirty after mount, so a
        // mount-time conditional would never attach the listener for changes made later.
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (shouldShow.current) {
                e.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return useMemo(
        () => ({
            shouldShowUnsavedChanges,
            setIsUnsavedChanges,
            showUnsavedChangesIfNeededOrClose,
        }),
        [shouldShowUnsavedChanges, setIsUnsavedChanges, showUnsavedChangesIfNeededOrClose]
    );
};
